
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BabyProfileDialog } from "@/components/BabyProfileDialog";
import { FamilyProfileDialog } from "@/components/FamilyProfileDialog";
import { getBabyProfile } from "@/utils/babyProfile";
import { getFamilyProfile } from "@/utils/family";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { BabyInfoSection } from "./BabyInfoSection";
import { ParentsInfoSection } from "./ParentsInfoSection";
import { SiblingsInfoSection } from "./SiblingsInfoSection";
import { GrandparentsInfoSection } from "./GrandparentsInfoSection";
import { useAuth } from "@/contexts/auth";

interface FamilyInformationCardProps {
  onProfileUpdate?: () => void;
}

export function FamilyInformationCard({ onProfileUpdate }: FamilyInformationCardProps) {
  const [babyProfile, setBabyProfile] = useState<any>(null);
  const [familyProfile, setFamilyProfile] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { user } = useAuth();

  // Use caching to avoid repeated profile loads
  const loadProfiles = useCallback(async () => {
    if (!user) {
      setBabyProfile(null);
      setFamilyProfile(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const cacheKey = `family_profiles_${user.id}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { babyData, familyData, timestamp } = JSON.parse(cachedData);
          const threeMinutesAgo = Date.now() - 3 * 60 * 1000;
          
          if (timestamp > threeMinutesAgo) {
            console.log('Using cached profile data');
            
            // Deduplicate grandparents in cached data if needed
            if (familyData && familyData.grandparents && Array.isArray(familyData.grandparents)) {
              const uniqueMap = new Map();
              familyData.grandparents.forEach(g => {
                if (g.id && !uniqueMap.has(g.id)) {
                  uniqueMap.set(g.id, g);
                }
              });
              familyData.grandparents = Array.from(uniqueMap.values());
            }
            
            setBabyProfile(babyData);
            setFamilyProfile(familyData);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing cached profile data', e);
          // Continue to fetch fresh data
        }
      }
      
      console.log('Fetching fresh profile data');
      const startTime = performance.now();
      
      // Use Promise.all to fetch profiles in parallel
      const [babyData, familyData] = await Promise.all([
        getBabyProfile(),
        getFamilyProfile()
      ]);
      
      const endTime = performance.now();
      console.log(`Profiles loaded in ${Math.round(endTime - startTime)}ms`);
      
      // Ensure no duplicate grandparents
      if (familyData && familyData.grandparents && Array.isArray(familyData.grandparents)) {
        const uniqueMap = new Map();
        familyData.grandparents.forEach(g => {
          const key = g.id || `${g.name}-${g.relation}`;
          if (key && !uniqueMap.has(key)) {
            uniqueMap.set(key, g);
          }
        });
        familyData.grandparents = Array.from(uniqueMap.values());
      }
      
      setBabyProfile(babyData);
      setFamilyProfile(familyData);
      
      // Cache the result
      sessionStorage.setItem(cacheKey, JSON.stringify({
        babyData,
        familyData,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error("Error loading profiles:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles, refreshKey]);

  const handleProfileUpdate = useCallback(() => {
    // Clear cache when profiles are updated
    if (user?.id) {
      sessionStorage.removeItem(`family_profiles_${user.id}`);
    }
    
    setRefreshKey(prevKey => prevKey + 1);
    if (onProfileUpdate) {
      onProfileUpdate();
    }
  }, [onProfileUpdate, user?.id]);

  // Memoize the card content to prevent unnecessary re-renders
  const cardContent = useMemo(() => {
    if (loading) {
      return (
        <div className="flex justify-center p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <BabyInfoSection 
          babyProfile={babyProfile} 
          onProfileUpdate={handleProfileUpdate} 
        />
        
        <ParentsInfoSection 
          familyProfile={familyProfile} 
          onProfileUpdate={handleProfileUpdate} 
        />
        
        <SiblingsInfoSection 
          familyProfile={familyProfile} 
          onProfileUpdate={handleProfileUpdate} 
        />
        
        <GrandparentsInfoSection 
          familyProfile={familyProfile} 
          onProfileUpdate={handleProfileUpdate} 
        />
      </div>
    );
  }, [loading, babyProfile, familyProfile, handleProfileUpdate]);

  return (
    <Card className="mb-8">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('family.information', language)}</CardTitle>
          <CardDescription>
            {language === 'ja' ? 'ご家族の詳細を閲覧・更新する' : 'View and update your family\'s details'}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <BabyProfileDialog onComplete={handleProfileUpdate} />
          <FamilyProfileDialog onComplete={handleProfileUpdate} />
        </div>
      </CardHeader>
      <CardContent>
        {cardContent}
      </CardContent>
    </Card>
  );
}
